import classNames from "classnames";
import {
  isSameDay,
  isSameMonth,
  isWithinInterval,
  addMonths,
  subMonths,
  isBefore,
  isAfter,
  startOfMonth,
  endOfMonth,
  addDays,
} from "date-fns";
import React, { useEffect, useRef } from "react";

import {
  validateInterval,
  datesInInterval,
  eachDayOfWeek,
  FirstDayOfWeek,
  DEFAULT_FIRST_DAY_OF_WEEK,
} from "../../lib/datetime";
import { getWeeksInMonth } from "../../lib/datetime";

import { Text } from "../../offering/text";
import { toDay } from "../../lib/datetime";
import { cssVariables, Divider, Icon, Spacing } from "../../components";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { Heading } from "../../offering/heading";
import { hasAvailableDateOnCurrentMonth } from "../utils/has_available_day_on_current_month";
import { parseDay } from "@/offering/day_utils";

export interface Interval {
  start: Date;
  end: Date;
}

interface DatePickerProps<TIsRange extends boolean = false> {
  /** Initial date used for navigation */
  startMonth?: Date;
  /** Selected value */
  value?: (TIsRange extends true ? Interval : Date) | null;
  onChange?: (value: TIsRange extends true ? Interval : Date) => void;
  isRange?: TIsRange;
  testID?: string;

  numberOfMonths?: number;
  orientation?: "vertical" | "horizontal";
  onNextVisibleDates?: (startDate: Date, endDate: Date) => void;
  isOutsideRange?: (date: Date) => boolean;
  isBlocked?: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  forceSingleDate?: boolean;
  blockNextRender?: (navigationDate: Date) => boolean;
  blockPrevRender?: (avigationDate: Date) => boolean;
  availableDates?: string[];
}

const VERTICAL_MONTHS_INCREMENT = 2;

function hasBlockedDate(
  interval: Interval,
  isBlocked?: (date: Date) => boolean
) {
  if (!isBlocked) return false;

  const dates = datesInInterval(interval, addDays);

  return dates.some(isBlocked);
}

export function DatePickerV2<TIsRange extends boolean = false>(
  props: DatePickerProps<TIsRange>
) {
  const {
    isRange = false,
    value,
    onChange,
    onNextVisibleDates,
    testID,
    isOutsideRange,
    isBlocked,
    numberOfMonths = 1,
    orientation = "horizontal",
    firstDayOfWeek,
    forceSingleDate,
    blockNextRender,
    blockPrevRender,
    availableDates,
  } = props;
  const { locale } = useI18n();
  const [interval, setInterval] = React.useState<Interval | null>(
    isRange ? (value as Interval) : null
  );
  const [navigationDate, setNavigationDate] = React.useState<Date>(
    value ? (isRange ? (value as any).start : value) : new Date()
  );

  const hasViewedMonthChangedRef = useRef(false);

  const blockNextArrow = !!blockNextRender && blockNextRender(navigationDate);
  const blockPrevArrow = !!blockPrevRender && blockPrevRender(navigationDate);
  const [noOfMonths, setNoOfMonths] = React.useState(numberOfMonths);

  React.useEffect(() => {
    setInterval(isRange ? (value as Interval) : null);
  }, [value, isRange]);

  const handleSelect = React.useCallback(
    (date: Date) => {
      if (!onChange) {
        return;
      }

      if (isRange) {
        if (value) {
          const val = value as Interval;

          // When only start was selected, it means user just selected end
          if (!forceSingleDate && isSameDay(val.start, val.end)) {
            if (isBefore(date, val.start)) {
              onChange({
                start: date,
                end: date,
              } as any);
            } else if (
              hasBlockedDate({ start: val.start, end: date }, isBlocked)
            ) {
              onChange({
                start: date,
                end: date,
              } as any);
            } else {
              onChange({
                start: val.start,
                end: date,
              } as any);
            }

            // When there is already an interval, reset to start only
          } else {
            onChange({
              start: date,
              end: date,
            } as any);
          }
        } else {
          onChange({
            start: date,
            end: date,
          } as any);
        }
      } else {
        onChange(date as any);
      }
    },
    [onChange, isRange, value, isBlocked, forceSingleDate]
  );

  useEffect(() => {
    if (onNextVisibleDates) {
      if (orientation === "horizontal") {
        onNextVisibleDates(navigationDate, addMonths(navigationDate, 1));
      } else {
        onNextVisibleDates(
          navigationDate,
          addMonths(navigationDate, noOfMonths)
        );
      }
    }
  }, [onNextVisibleDates, navigationDate, orientation, noOfMonths]);

  const handlePressNext = React.useCallback(() => {
    setNavigationDate(addMonths(navigationDate, 1));
  }, [navigationDate]);

  const handlePressPrevious = React.useCallback(() => {
    setNavigationDate(subMonths(navigationDate, 1));
  }, [navigationDate]);

  const handlePressMore = React.useCallback(() => {
    setNoOfMonths(noOfMonths + VERTICAL_MONTHS_INCREMENT);
  }, [noOfMonths]);

  const months = [];

  for (let index = 0; index < noOfMonths; index++) {
    months.push(index);
  }

  useEffect(() => {
    if (
      availableDates &&
      !hasAvailableDateOnCurrentMonth({
        availableDates: availableDates.map((dateString) =>
          parseDay(dateString)
        ),
        currentDate: navigationDate,
      }) &&
      isSameDay(navigationDate, new Date()) &&
      !hasViewedMonthChangedRef.current
    ) {
      handlePressNext();
      hasViewedMonthChangedRef.current = true;
    }
  }, [availableDates, handlePressNext, navigationDate, value]);

  return (
    <div data-testid={testID} className="date-picker-v2-root">
      {orientation === "horizontal" && (
        <div className="date-picker-v2-arrows-wrapper">
          <button
            className="date-picker-v2-navigation"
            onClick={handlePressPrevious}
            disabled={blockPrevArrow}
          >
            {locale.direction === "ltr" ? (
              <Icon
                color={
                  blockPrevArrow
                    ? cssVariables.buttonPrimaryDisabled
                    : undefined
                }
                name="chevron-left"
                size={24}
              />
            ) : (
              <Icon
                color={
                  blockPrevArrow
                    ? cssVariables.buttonPrimaryDisabled
                    : undefined
                }
                name="chevron-right"
                size={24}
              />
            )}
          </button>

          <button
            className="date-picker-v2-navigation"
            onClick={handlePressNext}
            disabled={blockNextArrow}
          >
            {locale.direction === "ltr" ? (
              <Icon
                color={
                  blockNextArrow
                    ? cssVariables.buttonPrimaryDisabled
                    : undefined
                }
                name="chevron-right"
                size={24}
              />
            ) : (
              <Icon
                color={
                  blockNextArrow
                    ? cssVariables.buttonPrimaryDisabled
                    : undefined
                }
                name="chevron-left"
                size={24}
              />
            )}
          </button>
        </div>
      )}
      <div
        className={
          orientation === "horizontal"
            ? "date-picker-v2-months-wrapper-horizontal"
            : "date-picker-v2-months-wrapper-vertical"
        }
      >
        {months.map((index) => {
          const month = addMonths(navigationDate, index);
          const isLast = index === months.length - 1;

          return (
            <React.Fragment key={month.getMonth()}>
              <div className="date-picker-v2-month-wrapper">
                <Month
                  firstDayOfWeek={firstDayOfWeek}
                  selected={
                    (value
                      ? isRange
                        ? interval
                        : { start: value, end: value }
                      : undefined) as Interval | undefined
                  }
                  date={month}
                  onSelect={handleSelect}
                  isOutsideRange={isOutsideRange}
                  isBlocked={isBlocked}
                />
              </div>
              {!isLast && <Spacing width={24} height={48} />}
            </React.Fragment>
          );
        })}
      </div>
      {orientation === "vertical" && (
        <>
          <Spacing height={48} />
          <button
            className="date-picker-v2-more-button"
            onClick={handlePressMore}
          >
            <Icon name="chevron-down" size={32} />
          </button>
        </>
      )}
    </div>
  );
}

interface MonthProps {
  onSelect?: (date: Date) => void;
  /**
   * Date with which we infer the month from
   */
  date?: Date;
  selected?: Interval;
  firstDayOfWeek?: FirstDayOfWeek;
  isOutsideRange?: (date: Date) => boolean;
  isBlocked?: (date: Date) => boolean;
}

export const Month = (props: MonthProps) => {
  const {
    date = new Date(),
    firstDayOfWeek = DEFAULT_FIRST_DAY_OF_WEEK,
    onSelect,
    selected,
    isOutsideRange = () => false,
    isBlocked = () => false,
  } = props;
  const { fbt } = useI18n();
  if (selected) validateInterval(selected);

  const som = startOfMonth(date);
  const eom = endOfMonth(date);
  const month = getWeeksInMonth(date, firstDayOfWeek);

  return (
    <div className="date-picker-v2-month-root">
      <div className="date-picker-v2-month-name-wrapper">
        <Heading size="sm">
          {fbt.formatDate(date, { month: "long", year: "numeric" })}
        </Heading>
      </div>
      <Spacing height={8} />
      <Divider />
      <Spacing height={8} />
      <WeekDates firstDayOfWeek={firstDayOfWeek} />
      <Spacing height={8} />
      <div>
        {month.weeks.map((week, weekIndex) => (
          <div className="date-picker-v2-week-container" key={weekIndex}>
            {week.days.map((day, dayIndex) => {
              const isWithinCurrentMonth = isSameMonth(day, date);
              const isSelectedStart =
                selected && isSameDay(day, selected.start);
              const isSelectedEnd = selected && isSameDay(day, selected.end);
              const isSelected = isSelectedStart || isSelectedEnd;
              const isWithinSelection =
                selected && !isSelected
                  ? isWithinInterval(day, selected)
                  : false;

              if (!isWithinCurrentMonth) {
                const hasSelectionNextMonth =
                  selected &&
                  isAfter(day, eom) &&
                  isAfter(selected.end, eom) &&
                  (isSameDay(selected.start, eom) ||
                    isBefore(selected.start, eom));

                const hasSelectionPreviousMonth =
                  selected &&
                  isBefore(day, som) &&
                  isBefore(selected.start, som) &&
                  (isSameDay(selected.end, som) || isAfter(selected.end, som));

                if (hasSelectionNextMonth || hasSelectionPreviousMonth) {
                  return <EmptyDay key={day.getDay()} isWithinSelection />;
                }

                return <EmptyDay key={day.getDay()} />;
              }

              return (
                <Day
                  key={day.getDay()}
                  onSelect={onSelect}
                  isWithinSelection={isWithinSelection}
                  index={dayIndex}
                  isSelected={isSelected}
                  isSelectedStart={isSelectedStart}
                  isSelectedEnd={isSelectedEnd}
                  date={day}
                  isOutsideRange={isOutsideRange(day)}
                  isBlocked={isBlocked(day)}
                  isToday={isSameDay(new Date(), day)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

interface DayProps {
  date: Date;
  index: number;
  isWithinSelection?: boolean;
  isSelected?: boolean;
  isSelectedStart?: boolean;
  isSelectedEnd?: boolean;
  isToday?: boolean;
  onSelect?: (date: Date) => void;
  isOutsideRange?: boolean;
  isBlocked?: boolean;
}

const Day = (props: DayProps) => {
  const {
    isWithinSelection,
    isSelected,
    date,
    onSelect = () => {},
    isOutsideRange,
    isBlocked,
    isSelectedStart,
    isSelectedEnd,
    isToday,
  } = props;
  const { fbt, locale } = useI18n();
  if (isOutsideRange || isBlocked) {
    return (
      <div className="date-picker-v2-day-root date-picker-v2-disabled">
        <div className="date-picker-v2-day-wrapper">
          <Text decoration="line-through" color="muted">
            {fbt.formatDateByPattern(date, "d")}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "date-picker-v2-day-root",
        isWithinSelection && "date-picker-v2-within-selection"
      )}
    >
      <button
        data-date={`day-${toDay(date)}`}
        className={classNames("date-picker-v2-day-button")}
        onClick={() => onSelect(date)}
      >
        {isSelectedStart && !isSelectedEnd && (
          <div
            className={classNames(
              "date-picker-v2-selected-edge",
              locale.direction === "ltr"
                ? "date-picker-v2-selected-start"
                : "date-picker-v2-selected-end"
            )}
          />
        )}
        {isSelectedEnd && !isSelectedStart && (
          <div
            className={classNames(
              "date-picker-v2-selected-edge",
              locale.direction === "ltr"
                ? "date-picker-v2-selected-end"
                : "date-picker-v2-selected-start"
            )}
          />
        )}
        <div
          className={classNames(
            "date-picker-v2-day-wrapper",
            isSelected && "date-picker-v2-selected"
          )}
        >
          <Text color={isSelected ? "white" : "default"}>
            {fbt.formatDateByPattern(date, "d")}
          </Text>
          {isToday && (
            <div className={classNames("date-picker-v2-today-marker")} />
          )}
        </div>
      </button>
    </div>
  );
};

interface EmptyDayProps {
  isWithinSelection?: boolean;
}

function EmptyDay(props: EmptyDayProps) {
  const { isWithinSelection } = props;

  return (
    <div
      className={classNames(
        "date-picker-v2-day-root",
        isWithinSelection && "date-picker-v2-within-selection"
      )}
    />
  );
}

interface WeekDatesProps {
  firstDayOfWeek?: FirstDayOfWeek;
  date?: Date;
}

function WeekDates(props: WeekDatesProps) {
  const { date = new Date(), firstDayOfWeek = DEFAULT_FIRST_DAY_OF_WEEK } =
    props;
  const { fbt } = useI18n();
  const dates = eachDayOfWeek(date, firstDayOfWeek);

  return (
    <div className="date-picker-v2-weekdates-container">
      {dates.map((date) => (
        <div
          className="date-picker-v2-weekdates-date-wrapper"
          key={date.toISOString()}
        >
          <Heading size="sm">{fbt.formatDateByPattern(date, "EEEEE")}</Heading>
        </div>
      ))}
    </div>
  );
}
